// y01 — Yubiley (jubilee variant)
//
// 25-year wedding anniversary — Madina & Tursun. The brief is a Vogue
// 25-year retrospective layered on top of a vintage postal stamp: dignified,
// classical, reverent of a long marriage. Cormorant Garamond italic carries
// the headlines on a platinum (#E0DCD2) ground; midnight (#1B1F2A) ink and
// silver (#B0AAA0) accent.
//
// Signature element: the platinum "XXV" Roman numerals on the hero get a
// gold-foil treatment via background-clip:text + a silver→platinum→silver
// linear-gradient that drifts slowly. prefers-reduced-motion freezes the
// shimmer to a static gradient — same look, no motion.
//
// Structure:
//   1. Hero (midnight backdrop, platinum XXV, names, silver dash years)
//   2. Countdown
//   3. Story — real Uzbek copy about 25 years, 3 children, growing old
//   4. Marriage Timeline — UNIQUE — vertical timeline with 6 milestones,
//      each on a Diamond ornament connector node
//   5. Gallery
//   6. Schedule (5 events, 18:00–23:00)
//   7. Map ("Heritage Hall" yubileу zali, Tashkent)
//   8. RSVP
//   9. Guestbook
//  10. Share
//  11. Footer
//
// Sound: 3 triggers — `chime` once when the timeline first appears in view
// (reverent ribbon-cutting blip), `tap` on the year-bullet hover/click in
// the timeline, `pop` on RSVP yes-confirm. All gated by useSound's user
// preference and prefers-reduced-motion via the sound module's defaults.

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion hook ──────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── IntersectionObserver-driven reveal ───────────────────────────────────

interface RevealProps {
  children: ReactNode;
  delay?: number;
  reduce: boolean;
  /** Fired exactly once when the element first crosses the threshold. */
  onShow?: () => void;
}

function Reveal({ children, delay = 0, reduce, onShow }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);
  const fired = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!fired.current) {
              fired.current = true;
              onShow?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onShow]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: reduce
          ? "none"
          : `opacity 1.1s ease-out ${delay}ms, transform 1.1s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Marriage timeline data — five-and-twenty years in six beats ─────────
// Year is presentation data (kept inline). Title and body live in the
// dictionary as variant.y01.timeline.<i>.title / .body.

const MILESTONE_YEARS = ["2001", "2003", "2008", "2015", "2020", "2026"] as const;

// ─── Component ────────────────────────────────────────────────────────────

export function JubileeVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const tt = useT();
  const { play } = useSound();

  // Wrap RSVP setter so a "yes" plays a single dignified `pop`.
  const wrappedRsvp = {
    ...ctx.rsvp,
    setRsvp: (v: string | null) => {
      const before = ctx.rsvp.rsvp;
      ctx.rsvp.setRsvp(v);
      if (v === "yes" && before !== "yes") play("pop");
    },
  };

  // Silver/platinum/midnight — read from template colors so a future
  // re-skin of the y01 palette flows through automatically.
  const silver = t.accent;
  const midnight = t.ink;
  const platinum = t.bg;

  return (
    <div className="v-y01">
      {/* ── Local CSS: gold-foil shimmer + timeline keyframes ────────── */}
      <style>{`
        @keyframes jubileeShimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .jubilee-xxv {
            animation: none !important;
            background-position: 50% 50% !important;
          }
        }

        .jubilee-xxv {
          /* Foil simulation: silver → platinum → silver gradient clipped to text. */
          background: linear-gradient(
            100deg,
            ${silver} 0%,
            ${platinum} 18%,
            #ffffff 30%,
            ${platinum} 42%,
            ${silver} 60%,
            ${platinum} 78%,
            ${silver} 100%
          );
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: jubileeShimmer 9s linear infinite;
          filter: drop-shadow(0 1px 0 rgba(0,0,0,0.18));
        }

        /* Timeline node — small platinum-on-midnight diamond bullet that
           glows briefly when the row enters view. We rely on the parent
           Reveal toggling .is-shown for the highlight pulse. */
        .jubilee-tl-row .jubilee-tl-bullet {
          transition: transform .6s ease-out, box-shadow .6s ease-out;
        }
        .jubilee-tl-row:hover .jubilee-tl-bullet,
        .jubilee-tl-row:focus-within .jubilee-tl-bullet {
          transform: scale(1.08);
          box-shadow: 0 0 0 3px ${silver}33;
        }

        /* Mobile lockdown — keep the stamp vibe but breathe at 360px. */
        @media (max-width: 768px) {
          .jubilee-hero {
            padding: 64px 18px 48px !important;
            min-height: 88vh !important;
          }
          .jubilee-hero-xxv {
            font-size: clamp(80px, 28vw, 140px) !important;
            letter-spacing: 0.08em !important;
          }
          .jubilee-hero-names {
            font-size: clamp(36px, 9vw, 72px) !important;
          }
          .jubilee-tl-axis {
            left: 32px !important;
          }
          /* The timeline bullets are positioned inline at left: 90px which
             assumes desktop proportions. On mobile the axis moves to 32px,
             so align the bullets to match. */
          .jubilee-tl-bullet {
            left: 32px !important;
          }
          .jubilee-tl-row {
            grid-template-columns: 64px 1fr !important;
            gap: 16px !important;
          }
          .jubilee-tl-year {
            font-size: 22px !important;
          }
          .jubilee-tl-title {
            font-size: 22px !important;
          }
          .jubilee-tl-body {
            font-size: 15px !important;
          }
          .v-y01 .v-y01-section {
            padding: 64px 18px !important;
          }
          .v-y01 .v-y01-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .v-y01 button,
          .v-y01 a[role="button"] {
            min-height: 44px;
          }
          /* Ensure hero content is visible when mobile animation-kill
             removes the Reveal translateY that would set opacity: 1 */
          .jubilee-hero .v-y01-reveal-content {
            opacity: 1 !important;
            transform: none !important;
          }
        }
        .v-y01 button:focus-visible,
        .v-y01 a:focus-visible {
          outline: 2px solid ${silver};
          outline-offset: 3px;
        }
      `}</style>

      {/* ─── HERO ──────────────────────────────────────────────────── */}
      <section
        className="jubilee-hero"
        style={{
          minHeight: "100vh",
          padding: "120px 32px 96px",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          background: midnight,
          color: platinum,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Faint silver hairline frame, tucked inside the section like
            the inner rule of a stamp. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: "min(40px, 6vw)",
            border: `1px solid ${silver}44`,
            pointerEvents: "none",
          }}
        />

        <Reveal reduce={reduce}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.5em",
                color: silver,
                marginBottom: 32,
              }}
            >
              25 YILLIK YUBILEY · ANNIVERSARY
            </div>

            {/* SIGNATURE — XXV with foil shimmer. */}
            <h1
              className="jubilee-hero-xxv jubilee-xxv"
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "clamp(140px, 22vw, 260px)",
                lineHeight: 0.95,
                letterSpacing: "0.06em",
                margin: 0,
              }}
            >
              XXV
            </h1>

            {/* Decorative diamond row — three diamonds in silver. */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 18,
                margin: "32px 0 28px",
                color: silver,
              }}
              aria-hidden
            >
              <O.Diamond size={10} color={silver} />
              <O.Diamond size={14} color={silver} />
              <O.Diamond size={10} color={silver} />
            </div>

            <h2
              className="jubilee-hero-names"
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "clamp(40px, 6.5vw, 72px)",
                margin: 0,
                color: platinum,
                letterSpacing: "0.01em",
                lineHeight: 1.1,
              }}
            >
              {t.names}
            </h2>

            {/* Years dash — silver hairline above and below. */}
            <div
              style={{
                margin: "36px auto 0",
                maxWidth: 320,
              }}
            >
              <div
                aria-hidden
                style={{
                  height: 1,
                  background: silver,
                  opacity: 0.55,
                  marginBottom: 14,
                }}
              />
              <div
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 22,
                  letterSpacing: "0.18em",
                  color: silver,
                  fontVariantNumeric: "lining-nums",
                }}
              >
                10.09.2001 — {t.date}
              </div>
              <div
                aria-hidden
                style={{
                  height: 1,
                  background: silver,
                  opacity: 0.55,
                  marginTop: 14,
                }}
              />
            </div>

            <div
              style={{
                marginTop: 40,
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: `${platinum}cc`,
              }}
            >
              {t.venue.toUpperCase()} · CELEBRATION
            </div>
          </div>
        </Reveal>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <StorySection
        t={t}
        eyebrow={tt("variant.y01.story.eyebrow")}
        title={tt("variant.y01.story.title")}
        body={tt("variant.y01.story.body")}
      />

      {/* ─── MARRIAGE TIMELINE — UNIQUE SECTION ────────────────────── */}
      <section
        style={{
          padding: "120px 32px",
          borderTop: `1px solid ${midnight}22`,
          background: `${silver}10`,
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <Reveal reduce={reduce} onShow={() => play("chime")}>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: silver,
                  marginBottom: 16,
                }}
              >
                25 YIL · TIMELINE
              </div>
              <h2
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 400,
                  margin: 0,
                  color: midnight,
                  letterSpacing: "-0.01em",
                }}
              >
                {tt("variant.y01.timeline.title")}
              </h2>
              <div
                aria-hidden
                style={{
                  height: 1,
                  width: 64,
                  background: silver,
                  margin: "20px auto 0",
                }}
              />
            </div>
          </Reveal>

          <div style={{ position: "relative" }}>
            {/* Vertical axis line — runs the height of the timeline. */}
            <div
              aria-hidden
              className="jubilee-tl-axis"
              style={{
                position: "absolute",
                top: 12,
                bottom: 12,
                left: 90,
                width: 1,
                background: `linear-gradient(to bottom, transparent 0%, ${silver}88 8%, ${silver}88 92%, transparent 100%)`,
              }}
            />

            {MILESTONE_YEARS.map((year, i) => (
              <Reveal key={year} reduce={reduce} delay={Math.min(i * 90, 360)}>
                <div
                  className="jubilee-tl-row"
                  tabIndex={0}
                  onClick={() => play("tap")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") play("tap");
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "100px 1fr",
                    gap: 32,
                    padding: "28px 0",
                    alignItems: "flex-start",
                    position: "relative",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <div
                    className="jubilee-tl-year"
                    style={{
                      fontFamily: t.font || "var(--font-display)",
                      fontStyle: "italic",
                      fontSize: 28,
                      fontWeight: 400,
                      color: silver,
                      letterSpacing: "0.04em",
                      textAlign: "right",
                      paddingTop: 4,
                      fontVariantNumeric: "lining-nums",
                    }}
                  >
                    {year}
                  </div>

                  {/* Diamond bullet sitting on the axis line. */}
                  <div
                    className="jubilee-tl-bullet"
                    aria-hidden
                    style={{
                      position: "absolute",
                      left: 90,
                      top: 32,
                      transform: "translate(-50%, 0)",
                      width: 22,
                      height: 22,
                      background: platinum,
                      border: `1px solid ${silver}`,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 0,
                    }}
                  >
                    <O.Diamond size={10} color={midnight} />
                  </div>

                  <div style={{ paddingLeft: 24 }}>
                    <div
                      className="jubilee-tl-title"
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 26,
                        fontWeight: 400,
                        color: midnight,
                        marginBottom: 8,
                        letterSpacing: "0.005em",
                      }}
                    >
                      {tt(`variant.y01.timeline.${i}.title`)}
                    </div>
                    <p
                      className="jubilee-tl-body"
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: 16,
                        lineHeight: 1.7,
                        color: `${midnight}cc`,
                        margin: 0,
                        fontStyle: "italic",
                      }}
                    >
                      {tt(`variant.y01.timeline.${i}.body`)}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      <Reveal reduce={reduce}>
        <ScheduleSection
          t={t}
          items={[
            ["18:00", tt("variant.y01.schedule.0.event")],
            ["19:00", tt("variant.y01.schedule.1.event")],
            ["20:00", tt("variant.y01.schedule.2.event")],
            ["21:30", tt("variant.y01.schedule.3.event")],
            ["23:00", tt("variant.y01.schedule.4.event")],
          ]}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine={tt("variant.y01.map.address")}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedRsvp.setRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label={tt("rsvp.title")}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <GuestbookSection
          t={t}
          isDark={isDark}
          wishes={ctx.guestbook.wishes}
          setWishes={ctx.guestbook.setWishes}
          guestName={ctx.guestbook.guestName}
          setGuestName={ctx.guestbook.setGuestName}
          wish={ctx.guestbook.wish}
          setWish={ctx.guestbook.setWish}
          slug={ctx.slug}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      <GuestFooter t={t} />
    </div>
  );
}
